/**
 * 계정 관련 API 서비스
 * - 이용자 정보 조회
 * - 회사 정보 조회
 * - 직원 관리 기능
 */
import { apiClient } from '@/api/client';
import { auth } from '@/features/auth/lib/auth';
import { UpdateCompanyInfoFormData } from '@/features/auth/types/api/auth';
import logger from '@/utils/logger';
import {
  CompanyResponse,
  Employee,
  EmployeeResponse,
  User,
  UserResponse
} from '../types/account';

class AccountService {
  /**
   * 현재 로그인한 이용자의 정보를 조회
   * @returns 이용자 정보
   * @throws 세션이 없거나 API 요청 실패 시 에러
   */
  async getUserInfo(): Promise<User> {
    try {
      const session = await auth();
      const userId = session?.user?.id;

      if (!userId) throw new Error('No id found');

      const response = await apiClient.get<UserResponse>(`/users/${userId}`);
      return response.data.user;
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }

  /**
   * 현재 로그인한 이용자의 회사 정보를 조회
   * @returns 회사 정보
   * @throws API 요청 실패 시 에러
   */
  async getCompanyInfo() {
    const response = await apiClient.get<CompanyResponse>('/users/me/company');
    return response.data.company;
  }

  /**
   * 회사 정보 수정
   * @param companyData - 수정할 회사 정보
   * @throws API 요청 실패 시 에러
   */
  async updateCompanyInfo(companyData: UpdateCompanyInfoFormData): Promise<void> {
    try {
      const response = await apiClient.patch('/users/me/company', companyData);
      return response.data;
    } catch (error) {
      logger.error(error);
      throw error;
    }
  }

  /**
   * 현재 로그인한 이용자의 회사 직원 목록을 조회
   * @returns 직원 목록
   * @throws API 요청 실패 시 에러
   */
  async getCompanyMembers(): Promise<Employee[]> {
    try {
      const response = await apiClient.get<EmployeeResponse>(
        '/users/me/company/employees',
      );
      return response.data.employees;
    } catch (error) {
      logger.error('getCompanyMembers', error);
      throw error;
    }
  }

  /**
   * 직원 승인 처리
   * @param id - 승인할 직원의 ID
   * @throws API 요청 실패 시 에러
   */
  async approveMember(id: number): Promise<void> {
    await apiClient.put(`/users/me/company/employees/${id}/approve`);
  }

  /**
   * 직원 비활성화 처리
   * @param id - 비활성화할 직원의 ID
   * @throws API 요청 실패 시 에러
   */
  async deactivateMember(id: number): Promise<void> {
    await apiClient.put(`/users/me/company/employees/${id}/deactivate`);
  }

  /**
   * 대표 계정 설정
   * @param id - 대표 계정으로 설정할 직원의 ID
   * @throws API 요청 실패 시 에러
   */
  async setOwner(id: number): Promise<void> {
    try {
      await apiClient.post(`/users/me/company/owner:transfer`, {
        new_owner_id: id,
      });
    } catch (error) {
      logger.error('accountService.setOwner', error);
      throw error;
    }
  }

  /**
   * 비밀번호 변경
   * @param currentPassword - 현재 비밀번호
   * @param newPassword - 새 비밀번호
   * @throws API 요청 실패 시 에러
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    return await apiClient.put('/users/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }
}

export const accountService = new AccountService();
